/**
 * Opt-in in-flight cap, keyed by source kind.
 *
 * An omitted limit acquires immediately. A granted permit stays held until
 * `release`, which is the fetch settling — not a consumer going away.
 * Waiters run in the order they asked.
 */
export interface KindPermit {
  release(): void;
}

function limitFor(configured: number | undefined): number {
  if (typeof configured !== 'number' || !Number.isFinite(configured)) {
    return Number.POSITIVE_INFINITY;
  }
  return Math.max(1, Math.trunc(configured));
}

const idlePermit: KindPermit = {
  release() {
    // This waiter never took a slot. Releasing it must not free someone else's.
  },
};

export function createKindGate(
  readLimits: () => Partial<Record<string, number>> | undefined,
): {
  acquire(kind: string, signal?: AbortSignal): Promise<KindPermit>;
  /** Re-read every cap and start calls that now fit. */
  refreshLimits(): void;
} {
  interface State {
    active: number;
    waiters: Array<() => void>;
  }
  const states = new Map<string, State>();

  const admitWaiters = (kind: string, current: State): void => {
    while (
      current.waiters.length > 0 &&
      current.active < limitFor(readLimits()?.[kind])
    ) {
      const next = current.waiters.shift();
      if (next === undefined) break;
      current.active += 1;
      next();
    }
  };

  return {
    refreshLimits(): void {
      states.forEach((state, kind) => {
        admitWaiters(kind, state);
      });
    },
    acquire(kind: string, signal?: AbortSignal): Promise<KindPermit> {
      if (signal?.aborted) return Promise.resolve(idlePermit);

      const limit = limitFor(readLimits()?.[kind]);
      let state = states.get(kind);
      if (!state) {
        state = { active: 0, waiters: [] };
        states.set(kind, state);
      }
      const current = state;

      const grant = (): KindPermit => {
        let released = false;
        return {
          release() {
            if (released) return;
            released = true;
            current.active -= 1;
            // A lowered cap drains the extra calls. A raised cap wakes the
            // calls that have been waiting, in the order they asked.
            admitWaiters(kind, current);
          },
        };
      };

      admitWaiters(kind, current);
      if (current.active < limit) {
        current.active += 1;
        return Promise.resolve(grant());
      }

      return new Promise((resolve) => {
        let settled = false;
        const admit = (): void => {
          if (settled) return;
          settled = true;
          signal?.removeEventListener('abort', cancel);
          resolve(grant());
        };
        const cancel = (): void => {
          if (settled) return;
          const index = current.waiters.indexOf(admit);
          if (index === -1) return;
          current.waiters.splice(index, 1);
          settled = true;
          resolve(idlePermit);
        };
        current.waiters.push(admit);
        if (signal === undefined) return;
        if (signal.aborted) {
          cancel();
          return;
        }
        signal.addEventListener('abort', cancel);
      });
    },
  };
}
